import { endOfDay, format, startOfDay, startOfYear, subDays } from "date-fns";

import { BillingCycleOption } from "./billingCycleOptions";

export type CoachVideoProjectDatePreset =
  | "last_30_days"
  | "last_60_days"
  | "last_90_days"
  | "ytd"
  | "billing_period"
  | "custom";

export const COACH_VIDEO_PROJECT_DATE_PRESET_LABELS: Record<CoachVideoProjectDatePreset, string> = {
  last_30_days: "Last 30 Days",
  last_60_days: "Last 60 Days",
  last_90_days: "Last 90 Days",
  ytd: "Year to Date",
  billing_period: "Billing Period",
  custom: "Custom Range",
};

export const COACH_VIDEO_PROJECT_DATE_PRESETS = Object.keys(
  COACH_VIDEO_PROJECT_DATE_PRESET_LABELS
) as CoachVideoProjectDatePreset[];

const ROLLING_DAY_COUNTS: Partial<Record<CoachVideoProjectDatePreset, number>> = {
  last_30_days: 30,
  last_60_days: 60,
  last_90_days: 90,
};

export interface CreatedDateBounds {
  createdAfter?: string;
  createdBefore?: string;
}

export function resolveCoachVideoProjectDatePreset(
  preset: CoachVideoProjectDatePreset,
  current?: { startDate: string | null; endDate: string | null },
  now: Date = new Date()
): { startDate: string | null; endDate: string | null } {
  const rollingDays = ROLLING_DAY_COUNTS[preset];
  if (rollingDays) {
    return {
      startDate: format(subDays(now, rollingDays), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  if (preset === "ytd") {
    return {
      startDate: format(startOfYear(now), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  if (preset === "custom") {
    if (current?.startDate || current?.endDate) {
      return { startDate: current.startDate ?? null, endDate: current.endDate ?? null };
    }
    return {
      startDate: format(subDays(now, 30), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  return { startDate: null, endDate: null };
}

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`);
}

export function createdDateBoundsFromRange(startDate: string | null, endDate: string | null): CreatedDateBounds {
  return {
    ...(startDate ? { createdAfter: startOfDay(parseLocalDate(startDate)).toISOString() } : {}),
    ...(endDate ? { createdBefore: endOfDay(parseLocalDate(endDate)).toISOString() } : {}),
  };
}

export function createdDateBoundsFromCycle(cycle: BillingCycleOption): CreatedDateBounds {
  return {
    createdAfter: startOfDay(cycle.start).toISOString(),
    createdBefore: endOfDay(cycle.end).toISOString(),
  };
}
